import { css, cx } from "@emotion/css";
import Image from "../../components/Image/Image";
import Link from "next/link";
import {
  TextBody,
  TextH2,
  TextH3,
} from "../../components/DesignSystem/DesignSystemText";
import CommunityStat from "./CommunityStat";
import { Community } from "../../@types/Community";
import { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { BASE_ASSET_URL } from "../../utils/asset";
import { GlobeAltIcon } from "@heroicons/react/solid";
import { useRouter } from "next/router";

interface Props {
  community: Community;
  className?: string;
}

const communityDescriptionId = "community-description";
const maxDescriptionHeight = 150;
const maskHeight = 100;
const discordIconUrl = `${BASE_ASSET_URL}/icons/discord.svg`;

const CommunityHeader = ({ community, className }: Props) => {
  const [showSeeMore, setShowSeeMore] = useState<boolean>(false);

  useEffect(() => {
    if (document === undefined) {
      return;
    }

    if (
      // @ts-ignore
      document?.getElementById(communityDescriptionId)?.clientHeight >
      maxDescriptionHeight
    ) {
      setShowSeeMore(true);
    }
  }, [community?.description]);

  return (
    <>
      <header
        className={cx(className, "flex flex-col items-center justify-center")}
      >
        <Link href={`/community/${community.uniqId}`}>
          <div
            className={cx(
              "flex items-center gap-x-4 cursor-pointer",
              !community?.name ? "opacity-0" : "",
            )}
          >
            <img src={community?.logoUrl ?? ""} height="50" width="50" alt="" />

            <TextH2>{community.name}</TextH2>
          </div>
        </Link>
        <div
          id={communityDescriptionId}
          className={cx(
            "overflow-hidden relative",
            showSeeMore ? `max-h-[${maxDescriptionHeight}px]` : "",
          )}
        >
          <TextBody className="!text-[18rem] mt-6 mb-7 max-w-[900px] text-center">
            {community.description}
          </TextBody>
          <div className="flex gap-x-8 items-center justify-center mb-5">
            <CommunityStat
              count={community.kudosCount}
              description="unique tokens"
            />
            <CommunityStat
              count={community.uniqueOwnersCount}
              description="unique owners"
            />
            <CommunityStat
              count={community.mintedKudosCount}
              description="total issued"
            />
          </div>
          {(community.websiteUrl || community.discordUrl) && (
            <div className="flex gap-x-[48rem] items-center justify-center mt-9">
              {community.websiteUrl && (
                <a href={community.websiteUrl} target="_blank" rel="noreferrer">
                  <GlobeAltIcon
                    className="cursor-pointer"
                    width="32rem"
                    height="32rem"
                  />
                </a>
              )}
              {community.discordUrl && (
                <a href={community.discordUrl} target="_blank" rel="noreferrer">
                  <Image
                    src={discordIconUrl}
                    className="cursor-pointer"
                    alt="discord"
                    width="28rem"
                    height="28rem"
                  />
                </a>
              )}
            </div>
          )}

          {showSeeMore && (
            <div
              id="blur"
              className={cx(
                `h-[${maskHeight}px]`,
                "w-full absolute bottom-0 opacity-80 bg-gradient-to-b from-transparent to-white",
              )}
            />
          )}
        </div>

        {showSeeMore && (
          <div
            className="mt-6"
            onClick={() => {
              setShowSeeMore(false);
            }}
          >
            <TextBody className="inline-flex items-center gap-x-[0.5rem] opacity-50 !font-semibold cursor-pointer">
              <span className="whitespace-nowrap">See more</span>
              <ChevronDownIcon className="h-6 w-6" />
            </TextBody>
          </div>
        )}
      </header>
    </>
  );
};

CommunityHeader.defaultProps = {
  community: {},
};

export default CommunityHeader;
